<template>
  <div>
    <Header
      :createUser="createUser"
      :logout="logout"
      :filters="filters"
      :onFilterChanged="onFilterChanged"
    />
    <Table
      :users="visibleUsers"
      :promoteUser="promoteUser"
      :demoteUser="demoteUser"
      :deleteUser="deleteUser"
    />
  </div>
</template>

<script>
import { auth } from "firebase";
import Header from "@/components/Users/Header.vue";
import Table from "@/components/Users/Table.vue";
import { User } from "../typings/user";

export default {
  name: "users",
  components: {
    Header,
    Table
  },
  data: () => {
    return {
      filters: {
        email: "",
        accountType: "All",
        accountPermissions: "All"
      }
    };
  },
  async mounted() {
    if (this.users.length > 0) {
      return;
    }
    try {
      let alert = this.$swal({
        title: "Loading users...",
        type: "info",
        showConfirmButton: false,
        showCancelButton: false,
        showCloseButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        allowEnterKey: false
      });
      await this.$store.dispatch("users/loadAll");
      alert.close();
    } catch {
      this.$swal.fire({
        title: "Unable to load users!",
        type: "error"
      });
    }
  },
  computed: {
    users() {
      return this.$store.getters["users/all"];
    },
    visibleUsers() {
      return this.users.filter(user => {
        if (
          this.filters.email.length > 0 &&
          !user.email.toLowerCase().includes(this.filters.email.toLowerCase())
        ) {
          return false;
        }
        if (this.filters.accountType !== "All") {
          if (
            (this.filters.accountType === "Speakers" && !user.isSpeaker) ||
            (this.filters.accountType !== "Speakers" && user.isSpeaker)
          ) {
            return false;
          }
        }
        if (
          this.filters.accountPermissions !== "All" &&
          this.filters.accountPermissions !== user.accountType
        ) {
          return false;
        }
        return true;
      });
    }
  },
  methods: {
    async createUser() {
      try {
        const result = await this.$swal({
          title: "Create new user",
          input: "email",
          inputPlaceholder: "Email Address",
          showCancelButton: true,
          showLoaderOnConfirm: true,
          preConfirm: async value => {
            try {
              await User.create(value);
              await this.$store.dispatch("users/loadAll");
            } catch (err) {
              this.handleError("Unable to create user", err);
            }
          },
          allowOutsideClick: () => !this.$swal.isLoading(),
          inputValidator: inputValue => {
            if (this.users.find(user => user.email === inputValue)) {
              return `A user already exists for ${inputValue}.`;
            } else if (inputValue.length === 0) {
              return "An email address is required to create a user.";
            }
          }
        });
        if (result.dismiss !== "cancel") {
          this.$swal({
            title: "User created!",
            type: "success"
          });
        }
      } catch (err) {
        this.handleError("Unable to create user", err);
      }
    },
    async promoteUser(user) {
      try {
        const result = await this.$swal({
          title: `Promote ${user.email}?`,
          type: "question",
          text: `${user.email} will now have access to the RAINN Speakers Bureau.`,
          showCancelButton: true,
          showLoaderOnConfirm: true,
          preConfirm: async value => {
            try {
              await user.promote();
            } catch (err) {
              this.handleError("Unable to promote user", err);
              return false;
            }
          },
          allowOutsideClick: () => !this.$swal.isLoading()
        });
        if (result.dismiss !== "cancel") {
          this.$swal({
            title: `${user.email} promoted!`,
            type: "success"
          });
        }
      } catch (err) {
        this.handleError("Unable to promote user", err);
      }
    },
    async demoteUser(user) {
      try {
        const result = await this.$swal({
          title: `Demote ${user.email}?`,
          type: "warning",
          text: `${user.email} will no longer have access to the RAINN Speakers Bureau.`,
          showCancelButton: true,
          showLoaderOnConfirm: true,
          preConfirm: async value => {
            try {
              await user.demote();
            } catch (err) {
              this.handleError("Unable to demote user", err);
              return false;
            }
          },
          allowOutsideClick: () => !this.$swal.isLoading()
        });
        if (result.dismiss !== "cancel") {
          this.$swal({
            title: `${user.email} demoted!`,
            type: "success"
          });
        }
      } catch (err) {
        this.handleError("Unable to demote user", err);
      }
    },
    async deleteUser(user) {
      try {
        const result = await this.$swal({
          title: `Delete ${user.email}?`,
          type: "warning",
          showCancelButton: true,
          showLoaderOnConfirm: true,
          preConfirm: async value => {
            try {
              await user.delete();
              await this.$store.dispatch("users/loadAll");
            } catch (err) {
              this.handleError("Unable to delete user", err);
              return false;
            }
          },
          allowOutsideClick: () => !this.$swal.isLoading()
        });
        if (result.dismiss !== "cancel") {
          this.$swal({
            title: `${user.email} deleted!`,
            type: "success"
          });
        }
      } catch (err) {
        this.handleError("Unable to delete user", err);
      }
    },
    async logout() {
      try {
        await this.$store.dispatch("auth/logout");
      } catch (err) {
        // Ignore errors
      } finally {
        this.$router.push("/login");
      }
    },
    handleError(title, err) {
      this.$swal({
        title: `${title}!`,
        text: "An unexpected error occurred.",
        type: "error"
      });
    },
    onFilterChanged(name, value) {
      this.$set(this.filters, name, value);
    }
  }
};
</script>
